import { createRoot } from 'react-dom/client';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { HashRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { initI18Next, initSentry, ToastsContextProvider } from '@utils';
import {
  localStorageAsync,
  OfficeUnavailableCause,
  shouldDehydrateQuery,
  startOfficeReadyWatchdog,
} from '@utils-global';
import { ErrorFallback } from '@components';
import App from './app';

const queryClient = new QueryClient();

const asyncStoragePersister = createAsyncStoragePersister({
  storage: localStorageAsync,
});

const rootElement: HTMLElement = document.getElementById('container');
const root = createRoot(rootElement);

// Lets integration tests, which load this entry once per test, start each test on a clean DOM.
export const unmount = () => root.unmount();

initSentry();

const OfficeUnavailable = ({ cause }: { cause: OfficeUnavailableCause }) => {
  const { t } = useTranslation();

  return (
    <ErrorFallback
      title={t('errorPage.officeUnavailable.title')}
      subtitle={t(
        cause === 'officeStringsFailed'
          ? 'errorPage.officeUnavailable.subtitleBlocked'
          : 'errorPage.officeUnavailable.subtitle',
      )}
    />
  );
};

let isOfficeUnavailable = false;

const renderOfficeUnavailable = (cause: OfficeUnavailableCause) => {
  isOfficeUnavailable = true;

  const render = () =>
    root.render(
      <FluentProvider theme={webLightTheme}>
        <OfficeUnavailable cause={cause} />
      </FluentProvider>,
    );

  initI18Next().then(render, render);
};

const cancelOfficeReadyWatchdog = startOfficeReadyWatchdog(renderOfficeUnavailable);

/* Render application after Office initializes */
Office.onReady(() => {
  cancelOfficeReadyWatchdog();

  if (isOfficeUnavailable) {
    return;
  }

  root.render(
    <FluentProvider theme={webLightTheme}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: asyncStoragePersister,
          dehydrateOptions: {
            shouldDehydrateQuery,
          },
        }}
      >
        <HashRouter>
          <ToastsContextProvider>
            <App />
          </ToastsContextProvider>
        </HashRouter>
      </PersistQueryClientProvider>
    </FluentProvider>,
  );
});

if ((module as any).hot) {
  (module as any).hot.accept('./app', () => {
    // eslint-disable-next-line global-require, @typescript-eslint/no-require-imports
    const NextApp = require('./app').default;
    root.render(NextApp);
  });
}
